import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ApiClient } from '../../api/client';
import {
  X, Server, ArrowRight, ArrowLeftRight, RefreshCw, CheckCircle2, AlertTriangle, ShieldAlert,
  Plus, Trash2, LogIn, Wrench, Eye, Play, Download, RotateCcw, History, Users, FileText,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
// EnvSyncWizard – környezetek közötti szinkron (Helyi ↔ VPS ↔ Éles)
//
// Lépések: környezetek → irány → szervezetek és párosítás → előnézet →
// jóváhagyás (élesnél beírással) → végrehajtás (automatikus mentéssel) →
// fájlátvitel → lezárás → jelentés / visszaállítás.
// A háttér: worker/routes/sync2.js; terv: docs/SZINKRON_ELEMZES_ES_TERV.md
//
// A távoli környezetek címét a böngésző megjegyzi (localStorage), a
// bejelentkezési tokeneket NEM – azok csak az ablak nyitva tartásáig élnek.
// ═══════════════════════════════════════════════════════════════

type Endpoint = { key: string; label: string; url: string; isProd: boolean; local?: boolean };
type Manifest = {
  instance_id: string; instance_name: string | null; prepared: boolean; missing: string[];
  tenants: { id: number; slug: string; name: string; uuid: string | null; is_demo: number; records: number; attachments: number; bytes: number }[];
};
type Decision = { mode: string; target_tenant_id?: number; new_slug?: string };

const STORE_KEY = 'kf_sync_endpoints_v2';
const fmtBytes = (n: number) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : n > 1024 ? `${(n / 1024).toFixed(0)} KB` : `${n} B`);
const ENTITY_LABEL: Record<string, string> = {
  page: 'Oldalak', field: 'Mezők', linked_entity: 'Kapcsolt szervek', regulation: 'Szervezeti jogszabályok',
  org_unit: 'Szervezeti egységek', position: 'Beosztások', person: 'Személyek', person_position: 'Betöltések',
  record: 'Bejegyzések', attachment: 'Mellékletek', record_regulation: 'Jogszabály-kapcsolatok',
  compliance_rule: 'Megfelelőségi szabályok', setting: 'Beállítások',
};
const MODE_LABEL: Record<string, string> = {
  sync: 'Szinkron (háromutas)', mirror: 'Tükrözés (a cél pontosan a forrás lesz)', create: 'Létrehozás',
  link: 'Összekapcsolás és tükrözés', new: 'Új szervezetként (más sluggal)', skip: 'Kihagyás',
};

function loadEndpoints(): Endpoint[] {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) || '[]');
    if (Array.isArray(raw)) return raw.filter((e) => e && e.url && e.label).map((e) => ({ ...e, local: false }));
  } catch { /* nincs mentett lista */ }
  return [];
}
function saveEndpoints(list: Endpoint[]) {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(list.filter((e) => !e.local).map(({ key, label, url, isProd }) => ({ key, label, url, isProd })))); } catch { /* ignore */ }
}

export const EnvSyncWizard: React.FC<{ isOpen: boolean; onClose: () => void; onOpenLegacy?: () => void }> = ({ isOpen, onClose, onOpenLegacy }) => {
  const localEp: Endpoint = { key: 'local', label: 'Ez a környezet', url: '', isProd: false, local: true };
  const [eps, setEps] = useState<Endpoint[]>(() => [localEp, ...loadEndpoints()]);
  const [tokens, setTokens] = useState<Record<string, string>>({});
  // a tokenek ref-ben is: az aszinkron hívások így nem régi állapotot látnak
  const tokensRef = useRef<Record<string, string>>({});
  const [mans, setMans] = useState<Record<string, Manifest | null>>({});
  const [epErr, setEpErr] = useState<Record<string, string>>({});
  const [loginFor, setLoginFor] = useState<string | null>(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '', code: '', temp: '' });
  const [newEp, setNewEp] = useState({ label: '', url: '', isProd: false });

  const [srcKey, setSrcKey] = useState('local');
  const [dstKey, setDstKey] = useState('');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [includeUsers, setIncludeUsers] = useState(false);
  const [includeDemo, setIncludeDemo] = useState(false);
  const [policy, setPolicy] = useState<'source' | 'target'>('source');

  const [step, setStep] = useState<'setup' | 'preview' | 'running' | 'report' | 'runs'>('setup');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pkg, setPkg] = useState<any>(null);
  const [preview, setPreview] = useState<any>(null);
  const [confirmText, setConfirmText] = useState('');
  const [progress, setProgress] = useState<{ text: string; done: number; total: number }>({ text: '', done: 0, total: 0 });
  const [report, setReport] = useState<any>(null);
  const [runs, setRuns] = useState<any[]>([]);
  const [runsKey, setRunsKey] = useState('local');

  const ep = (k: string) => eps.find((e) => e.key === k)!;
  const tokenOf = (k: string) => (k === 'local' ? ApiClient.getToken() || '' : tokensRef.current[k] || tokens[k] || '');

  // ── HTTP segédek (helyi: relatív /api; távoli: <url>/api) ────
  const call = async (k: string, method: string, path: string, body?: any): Promise<any> => {
    const base = k === 'local' ? '' : ep(k).url.replace(/\/+$/, '');
    const res = await fetch(`${base}/api${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', ...(tokenOf(k) ? { Authorization: `Bearer ${tokenOf(k)}` } : {}) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    let data: any = {};
    try { data = await res.json(); } catch { data = {}; }
    if (!res.ok) { const e: any = new Error(data.error || `HTTP ${res.status}`); e.status = res.status; e.data = data; throw e; }
    return data;
  };
  const rawFetch = (k: string, path: string, init: RequestInit = {}) => {
    const base = k === 'local' ? '' : ep(k).url.replace(/\/+$/, '');
    return fetch(`${base}/api${path}`, { ...init, headers: { ...(init.headers || {}), Authorization: `Bearer ${tokenOf(k)}` } });
  };

  const loadManifest = async (k: string) => {
    setEpErr((p) => ({ ...p, [k]: '' }));
    try {
      const m = await call(k, 'GET', '/sync2/manifest');
      setMans((p) => ({ ...p, [k]: m }));
    } catch (e: any) {
      setMans((p) => ({ ...p, [k]: null }));
      setEpErr((p) => ({ ...p, [k]: e.status === 401 ? 'Bejelentkezés szükséges' : e.status === 403 ? 'Csak rendszer-superadmin használhatja (vagy zárt mód: VPN szükséges)' : e.message }));
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setStep('setup'); setError(null); setPreview(null); setReport(null);
    loadManifest('local');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Szervezetek és párosítás javaslat ───────────────────────
  const srcMan = mans[srcKey];
  const dstMan = dstKey ? mans[dstKey] : null;
  const pairing = useMemo(() => {
    const out: Record<string, { status: 'paired' | 'slug' | 'none'; target?: Manifest['tenants'][number] }> = {};
    if (!srcMan || !dstMan) return out;
    for (const t of srcMan.tenants) {
      if (!t.uuid) continue;
      const byUuid = dstMan.tenants.find((x) => x.uuid === t.uuid);
      if (byUuid) { out[t.uuid] = { status: 'paired', target: byUuid }; continue; }
      const bySlug = dstMan.tenants.find((x) => x.slug === t.slug);
      out[t.uuid] = bySlug ? { status: 'slug', target: bySlug } : { status: 'none' };
    }
    return out;
  }, [srcMan, dstMan]);

  useEffect(() => {
    // alapértelmezett döntések az új párosításhoz
    const d: Record<string, Decision> = {};
    for (const [u, p] of Object.entries(pairing)) {
      d[u] = decisions[u] && decisions[u].mode !== '' ? decisions[u] :
        p.status === 'paired' ? { mode: 'sync' } : p.status === 'none' ? { mode: 'create' } : { mode: '' };
    }
    setDecisions(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairing]);

  if (!isOpen) return null;

  // ── Környezetek kezelése ─────────────────────────────────────
  const addEndpoint = () => {
    const url = newEp.url.trim().replace(/\/+$/, '').replace(/\/api$/, '');
    if (!newEp.label.trim() || !/^https?:\/\//i.test(url)) { setError('Adj meg nevet és http(s) címet'); return; }
    const e: Endpoint = { key: `ep_${Date.now()}`, label: newEp.label.trim(), url, isProd: newEp.isProd };
    const list = [...eps, e];
    setEps(list); saveEndpoints(list); setNewEp({ label: '', url: '', isProd: false }); setError(null);
  };
  const removeEndpoint = (k: string) => {
    const list = eps.filter((e) => e.key !== k);
    setEps(list); saveEndpoints(list);
    if (srcKey === k) setSrcKey('local');
    if (dstKey === k) setDstKey('');
  };
  const doLogin = async () => {
    if (!loginFor) return;
    setBusy('login'); setError(null);
    try {
      let tok = '';
      if (loginForm.temp) {
        const r = await call(loginFor, 'POST', '/auth/2fa/login-verify', { temp_token: loginForm.temp, code: loginForm.code });
        tok = r.token;
      } else {
        const r = await call(loginFor, 'POST', '/auth/login', { email: loginForm.email, password: loginForm.password });
        if (r.requires_2fa && r.temp_token) { setLoginForm((p) => ({ ...p, temp: r.temp_token })); setBusy(null); return; }
        tok = r.token;
      }
      const k = loginFor;
      tokensRef.current = { ...tokensRef.current, [k]: tok };
      setTokens((p) => ({ ...p, [k]: tok }));
      setLoginFor(null); setLoginForm({ email: '', password: '', code: '', temp: '' });
      await loadManifest(k);
    } catch (e: any) {
      setError(`Bejelentkezés sikertelen: ${e.message}`);
    }
    setBusy(null);
  };
  const doPrepare = async (k: string) => {
    const name = window.prompt('A környezet megjelenített neve (pl. Helyi, VPS, Éles):', ep(k).local ? 'Helyi' : ep(k).label) || undefined;
    setBusy(`prep_${k}`); setError(null);
    try { await call(k, 'POST', '/sync2/prepare', { instance_name: name }); await loadManifest(k); }
    catch (e: any) { setError(`Előkészítés sikertelen: ${e.message}`); }
    setBusy(null);
  };

  // ── Előnézet ────────────────────────────────────────────────
  const chosenUuids = Object.keys(selected).filter((u) => selected[u]);
  const undecided = chosenUuids.filter((u) => !decisions[u]?.mode || (decisions[u].mode === 'new' && !decisions[u].new_slug) || (decisions[u].mode === 'link' && !decisions[u].target_tenant_id));
  const canPreview = srcMan?.prepared && dstMan?.prepared && srcKey !== dstKey && chosenUuids.length > 0 && undecided.length === 0;

  const buildDecisions = () => Object.fromEntries(chosenUuids.map((u) => [u, decisions[u]]));
  const doPreview = async () => {
    setBusy('preview'); setError(null); setPreview(null);
    try {
      const ex = await call(srcKey, 'POST', '/sync2/export', { tenant_uuids: chosenUuids, include_users: includeUsers });
      setPkg(ex.package);
      const pv = await call(dstKey, 'POST', '/sync2/preview', {
        package: ex.package, decisions: buildDecisions(), conflict_policy: policy, include_users: includeUsers, include_demo: includeDemo,
      });
      setPreview(pv); setStep('preview'); setConfirmText('');
    } catch (e: any) { setError(e.message); }
    setBusy(null);
  };

  // ── Végrehajtás ─────────────────────────────────────────────
  const doApply = async () => {
    setStep('running'); setError(null);
    try {
      setProgress({ text: 'Mentés a célban és az adatok alkalmazása…', done: 0, total: 1 });
      const ap = await call(dstKey, 'POST', '/sync2/apply', {
        package: pkg, decisions: buildDecisions(), conflict_policy: policy, include_users: includeUsers, include_demo: includeDemo,
        plan_hash: preview.plan_hash, confirmed: true,
      });
      const files = ap.files || [];
      let sent = 0; const failed: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setProgress({ text: `Fájlok átvitele: ${f.filename}`, done: i, total: files.length });
        try {
          const g = await rawFetch(srcKey, `/sync2/file?key=${encodeURIComponent(f.src_key)}`);
          if (!g.ok) throw new Error(`forrás: HTTP ${g.status}`);
          const buf = await g.arrayBuffer();
          const p = await rawFetch(dstKey, `/sync2/file?run=${ap.run_id}&key=${encodeURIComponent(f.dst_key)}`, { method: 'POST', body: buf });
          if (!p.ok) throw new Error(`cél: HTTP ${p.status}`);
          sent++;
        } catch (e: any) { failed.push(`${f.filename}: ${e.message}`); }
      }
      setProgress({ text: 'Lezárás…', done: files.length, total: files.length || 1 });
      const fin = await call(dstKey, 'POST', '/sync2/finish', { run_id: ap.run_id });
      let stateNote = '';
      try {
        await call(srcKey, 'POST', '/sync2/record-state', { peer: dstMan!.instance_id, tenants: fin.state?.tenants || [] });
      } catch (e: any) { stateNote = `A forrás szinkronnaplója nem frissült (${e.message}); a következő szinkron első szinkronként kezeli az eltéréseket.`; }
      setReport({ run_id: ap.run_id, backups: ap.backups, files: files.length, sent, failed, missing: fin.missing_files || [], warnings: ap.warnings || [], stateNote });
      setStep('report');
      loadManifest(srcKey); loadManifest(dstKey);
    } catch (e: any) {
      setError(e.data?.plan_changed ? 'A cél adatai az előnézet óta megváltoztak – készíts új előnézetet.' : e.message);
      setStep('preview');
    }
  };

  const doRollback = async (k: string, runId: string) => {
    if (!window.confirm('Biztosan visszaállítod ezt a szinkronfutást? A cél környezetben a futás előtti állapot áll vissza.')) return;
    setBusy(`rb_${runId}`); setError(null);
    try { const r = await call(k, 'POST', '/sync2/rollback', { run_id: runId }); window.alert(`Visszaállítva (${r.steps} lépés).`); await loadRuns(k); }
    catch (e: any) { setError(e.message); }
    setBusy(null);
  };
  const loadRuns = async (k: string) => {
    setRunsKey(k);
    try { const r = await call(k, 'GET', '/sync2/runs'); setRuns(r.data || []); } catch (e: any) { setError(e.message); }
  };
  const downloadBackup = async (k: string, runId: string, tenantId: number) => {
    const r = await rawFetch(k, `/sync2/backup?run=${runId}&tenant=${tenantId}`);
    if (!r.ok) { setError(`A mentés nem tölthető le (HTTP ${r.status})`); return; }
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = `szinkron-elotti-mentes-${tenantId}.sql`; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  };

  const dstIsProd = dstKey ? ep(dstKey)?.isProd : false;
  const confirmOk = !dstIsProd || confirmText.trim().toUpperCase() === 'ÉLES';

  // ── Megjelenítés ────────────────────────────────────────────
  const EpCard: React.FC<{ e: Endpoint }> = ({ e }) => {
    const m = mans[e.key];
    return (
      <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Server className="w-4 h-4 text-indigo-500 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                {e.label}{m?.instance_name ? ` · ${m.instance_name}` : ''}
                {e.isProd && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-600 border border-rose-500/30">ÉLES</span>}
              </div>
              <div className="text-[11px] text-slate-500 font-mono truncate">{e.local ? window.location.origin : e.url}</div>
            </div>
          </div>
          {!e.local && (
            <button onClick={() => removeEndpoint(e.key)} className="p-1 text-slate-400 hover:text-rose-500 cursor-pointer" title="Eltávolítás"><Trash2 className="w-3.5 h-3.5" /></button>
          )}
        </div>
        {m ? (
          <div className="flex items-center justify-between gap-2 text-xs">
            {m.prepared ? (
              <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Kész · {m.tenants.length} szervezet</span>
            ) : (
              <span className="text-amber-600 flex items-center gap-1" title={m.missing.join('\n')}><AlertTriangle className="w-3.5 h-3.5" /> Előkészítés szükséges</span>
            )}
            <div className="flex gap-1">
              {!m.prepared && (
                <button onClick={() => doPrepare(e.key)} disabled={!!busy} className="px-2 py-1 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30 font-semibold flex items-center gap-1 cursor-pointer disabled:opacity-50">
                  <Wrench className="w-3 h-3" /> Előkészítés
                </button>
              )}
              <button onClick={() => loadManifest(e.key)} className="p-1 text-slate-400 hover:text-slate-600 cursor-pointer" title="Frissítés"><RefreshCw className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="text-slate-500">{epErr[e.key] || 'Nincs kapcsolat'}</span>
            {e.local ? (
              <button onClick={() => loadManifest(e.key)} className="px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer">Újra</button>
            ) : (
              <button onClick={() => { setLoginFor(e.key); setLoginForm({ email: '', password: '', code: '', temp: '' }); }} className="px-2 py-1 rounded-lg bg-indigo-600 text-white font-semibold flex items-center gap-1 cursor-pointer">
                <LogIn className="w-3 h-3" /> Belépés
              </button>
            )}
          </div>
        )}
        {!m?.prepared && m && (
          <p className="text-[10.5px] text-slate-500 leading-snug">Az előkészítés a szinkronhoz szükséges globális azonosítókat és naplótáblákat hozza létre. Üzleti adat nem változik.</p>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/60 backdrop-blur-xs">
      <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-3 bg-white/95 dark:bg-slate-900/95 border-b border-slate-200 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-indigo-600" />
            <h2 className="text-base font-bold text-slate-900 dark:text-white">Környezetek közötti szinkronizáció</h2>
          </div>
          <div className="flex items-center gap-2">
            {onOpenLegacy && (
              <button onClick={onOpenLegacy} className="text-[11px] text-slate-400 hover:text-slate-600 underline cursor-pointer" title="A korábbi, egyszerűbb csomag alapú szinkron">Régi szinkron</button>
            )}
            <button onClick={() => { setStep('runs'); loadRuns(dstKey || 'local'); }} className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center gap-1 cursor-pointer"><History className="w-3.5 h-3.5" /> Korábbi futások</button>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 cursor-pointer"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {error && (
            <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-sm text-rose-700 dark:text-rose-300 flex gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{error}</div>
          )}

          {step === 'setup' && (
            <>
              {/* 1. Környezetek */}
              <section className="space-y-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">1. Környezetek</h3>
                <div className="grid sm:grid-cols-3 gap-2">
                  {eps.map((e) => <EpCard key={e.key} e={e} />)}
                </div>
                <div className="flex flex-wrap items-end gap-2 p-3 rounded-xl border border-dashed border-slate-300 dark:border-slate-700">
                  <input value={newEp.label} onChange={(e) => setNewEp({ ...newEp, label: e.target.value })} placeholder="Név (pl. VPS, Éles)" className="px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs w-36" />
                  <input value={newEp.url} onChange={(e) => setNewEp({ ...newEp, url: e.target.value })} placeholder="https://… (a /api nélkül)" className="px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs flex-1 min-w-[200px] font-mono" />
                  <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={newEp.isProd} onChange={(e) => setNewEp({ ...newEp, isProd: e.target.checked })} /> Éles környezet</label>
                  <button onClick={addEndpoint} className="px-3 py-1.5 rounded-lg bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 text-xs font-semibold flex items-center gap-1 cursor-pointer"><Plus className="w-3.5 h-3.5" /> Hozzáadás</button>
                </div>
                {!ep('local').isProd && (
                  <label className="text-[11px] text-slate-500 flex items-center gap-1.5">
                    <input type="checkbox" checked={eps[0].isProd} onChange={(e) => setEps((p) => p.map((x) => (x.local ? { ...x, isProd: e.target.checked } : x)))} />
                    Ez a környezet az éles rendszer (beírásos megerősítést kér, ha ide írunk)
                  </label>
                )}
              </section>

              {/* 2. Irány */}
              <section className="space-y-2">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">2. Irány</h3>
                <div className="flex flex-wrap items-center gap-2">
                  <select value={srcKey} onChange={(e) => { setSrcKey(e.target.value); setSelected({}); }} className="px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
                    {eps.map((e) => <option key={e.key} value={e.key}>Forrás: {e.label}</option>)}
                  </select>
                  <button onClick={() => { const s = srcKey; setSrcKey(dstKey || 'local'); setDstKey(s); setSelected({}); }} disabled={!dstKey} className="p-2 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer disabled:opacity-40" title="Irány megfordítása"><ArrowLeftRight className="w-4 h-4" /></button>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                  <select value={dstKey} onChange={(e) => setDstKey(e.target.value)} className="px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
                    <option value="">Cél kiválasztása…</option>
                    {eps.filter((e) => e.key !== srcKey).map((e) => <option key={e.key} value={e.key}>Cél: {e.label}{e.isProd ? ' (ÉLES)' : ''}</option>)}
                  </select>
                  {dstIsProd && <span className="text-xs text-rose-600 flex items-center gap-1"><ShieldAlert className="w-4 h-4" /> Éles cél: kötelező mentés + beírásos megerősítés</span>}
                </div>
                {srcMan && dstMan && srcMan.instance_id === dstMan.instance_id && (
                  <p className="text-xs text-rose-600">A forrás és a cél ugyanaz a környezet.</p>
                )}
              </section>

              {/* 3. Szervezetek */}
              {srcMan && dstMan && srcMan.prepared && dstMan.prepared && srcMan.instance_id !== dstMan.instance_id && (
                <section className="space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">3. Szervezetek és párosítás</h3>
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      <button onClick={() => setSelected(Object.fromEntries(srcMan.tenants.filter((t) => t.uuid && (includeDemo || !t.is_demo)).map((t) => [t.uuid!, true])))} className="px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer">Összes kijelölése</button>
                      <button onClick={() => setSelected({})} className="px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer">Egyik sem</button>
                      <label className="flex items-center gap-1.5"><input type="checkbox" checked={includeDemo} onChange={(e) => setIncludeDemo(e.target.checked)} /> DEMO / homokozó szervezetek is</label>
                      <label className="flex items-center gap-1.5"><input type="checkbox" checked={includeUsers} onChange={(e) => setIncludeUsers(e.target.checked)} /> <Users className="w-3.5 h-3.5" /> Felhasználók is (jelszóval)</label>
                      <label className="flex items-center gap-1.5">Ütközésnél:
                        <select value={policy} onChange={(e) => setPolicy(e.target.value as any)} className="px-1.5 py-1 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900">
                          <option value="source">a forrás nyer</option>
                          <option value="target">a cél nyer</option>
                        </select>
                      </label>
                    </div>
                  </div>
                  {includeUsers && (
                    <p className="text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-2">
                      A célban még nem létező felhasználók a jelszavukkal együtt jönnek létre (2FA titok nélkül). Meglévő felhasználó soha nem módosul. Superadmin és rendszerfiók nem kerül át.
                    </p>
                  )}
                  <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100 dark:bg-slate-900 text-slate-500 text-left">
                        <tr><th className="p-2 w-8"></th><th className="p-2">Forrás szervezet</th><th className="p-2">Tartalom</th><th className="p-2">Cél állapota</th><th className="p-2">Teendő</th></tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900">
                        {srcMan.tenants.filter((t) => t.uuid).map((t) => {
                          const p = pairing[t.uuid!];
                          const d = decisions[t.uuid!] || { mode: '' };
                          const disabledDemo = !!t.is_demo && !includeDemo;
                          return (
                            <tr key={t.uuid!} className={disabledDemo ? 'opacity-50' : ''}>
                              <td className="p-2"><input type="checkbox" disabled={disabledDemo} checked={!!selected[t.uuid!]} onChange={(e) => setSelected((s) => ({ ...s, [t.uuid!]: e.target.checked }))} /></td>
                              <td className="p-2"><div className="font-semibold text-slate-800 dark:text-slate-100">{t.name}</div><div className="font-mono text-slate-400">/{t.slug}{t.is_demo ? ' · DEMO' : ''}</div></td>
                              <td className="p-2 text-slate-500">{t.records} bejegyzés · {t.attachments} fájl ({fmtBytes(t.bytes)})</td>
                              <td className="p-2">
                                {p?.status === 'paired' && <span className="text-emerald-600">Párosítva: {p.target!.name}</span>}
                                {p?.status === 'slug' && <span className="text-amber-600">Azonos slug, de MÁS szervezet lehet: „{p.target!.name}”</span>}
                                {p?.status === 'none' && <span className="text-slate-500">Nem létezik</span>}
                              </td>
                              <td className="p-2 space-y-1">
                                <select value={d.mode} onChange={(e) => setDecisions((x) => ({ ...x, [t.uuid!]: { mode: e.target.value, target_tenant_id: e.target.value === 'link' ? p?.target?.id : undefined, new_slug: e.target.value === 'new' ? `${t.slug}-${(srcMan.instance_name || 'masolat').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 12)}` : undefined } }))}
                                  className={`w-full px-1.5 py-1 rounded border bg-white dark:bg-slate-950 ${!d.mode && selected[t.uuid!] ? 'border-rose-400' : 'border-slate-300 dark:border-slate-700'}`}>
                                  {p?.status === 'slug' && <option value="">– Döntés szükséges –</option>}
                                  {(p?.status === 'paired' ? ['sync', 'mirror', 'skip'] : p?.status === 'slug' ? ['link', 'new', 'skip'] : ['create', 'new', 'skip']).map((m) => <option key={m} value={m}>{MODE_LABEL[m]}</option>)}
                                </select>
                                {d.mode === 'new' && (
                                  <input value={d.new_slug || ''} onChange={(e) => setDecisions((x) => ({ ...x, [t.uuid!]: { ...d, new_slug: e.target.value.toLowerCase() } }))} placeholder="új slug" className="w-full px-1.5 py-1 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 font-mono" />
                                )}
                                {d.mode === 'link' && (
                                  <select value={d.target_tenant_id || ''} onChange={(e) => setDecisions((x) => ({ ...x, [t.uuid!]: { ...d, target_tenant_id: Number(e.target.value) } }))} className="w-full px-1.5 py-1 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950">
                                    {dstMan.tenants.filter((x) => !srcMan.tenants.some((s) => s.uuid && s.uuid === x.uuid)).map((x) => <option key={x.id} value={x.id}>{x.name} (/{x.slug})</option>)}
                                  </select>
                                )}
                                {d.mode === 'link' && <div className="text-[10.5px] text-amber-600">A cél szervezet tartalma a forráséval lesz azonos (a többlet visszaállíthatóan törlődik).</div>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">{chosenUuids.length} szervezet kijelölve{undecided.length ? ` · ${undecided.length} döntésre vár` : ''}</span>
                    <button onClick={doPreview} disabled={!canPreview || !!busy} className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold flex items-center gap-2 cursor-pointer disabled:opacity-50">
                      {busy === 'preview' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />} Előnézet (még semmi nem változik)
                    </button>
                  </div>
                </section>
              )}
            </>
          )}

          {step === 'preview' && preview && (
            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">Előnézet: {ep(srcKey).label} → {ep(dstKey).label}</h3>
                <button onClick={() => setStep('setup')} className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer">Vissza</button>
              </div>
              {preview.summary.tenants.map((t: any) => (
                <div key={t.source_uuid} className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-sm font-semibold">{t.name} <span className="font-mono text-xs text-slate-400">/{t.new_slug || t.source_slug}</span></div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-700 dark:text-indigo-300">{MODE_LABEL[t.mode] || t.mode}{t.skipped_reason ? ` – ${t.skipped_reason}` : ''}</span>
                  </div>
                  {t.mode !== 'skip' && (
                    <table className="w-full text-xs">
                      <thead className="text-slate-500 text-left"><tr><th className="py-1">Adat</th><th>Új</th><th>Módosul</th><th>Törlődik*</th><th>Változatlan</th><th>Cél megtartja</th><th>Ütközés</th></tr></thead>
                      <tbody>
                        {Object.entries(t.counts).filter(([, c]: any) => c.insert + c.update + c.delete + c.same + c.keep_target + c.conflict > 0).map(([e, c]: any) => (
                          <tr key={e} className="border-t border-slate-100 dark:border-slate-800">
                            <td className="py-1">{ENTITY_LABEL[e] || e}</td><td className="text-emerald-600">{c.insert || ''}</td><td className="text-blue-600">{c.update || ''}</td>
                            <td className="text-rose-600">{c.delete || ''}</td><td className="text-slate-400">{c.same || ''}</td><td className="text-slate-500">{c.keep_target || ''}</td><td className="text-amber-600 font-semibold">{c.conflict || ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {t.conflicts_total > 0 && (
                    <details className="text-xs">
                      <summary className="cursor-pointer text-amber-700 dark:text-amber-400 font-semibold">{t.conflicts_total} ütközés – {policy === 'source' ? 'a forrás változata kerül be' : 'a cél változata marad'}</summary>
                      <ul className="mt-1 space-y-0.5 max-h-40 overflow-y-auto">{t.conflicts.map((c: any, i: number) => <li key={i} className="text-slate-600 dark:text-slate-400">{ENTITY_LABEL[c.e] || c.e}: {String(c.label).slice(0, 90)} – {c.kind || 'eltérés'}</li>)}</ul>
                    </details>
                  )}
                </div>
              ))}
              {preview.summary.tenants.every((t: any) => t.mode === 'skip' || Object.values(t.counts || {}).every((c: any) => !c.insert && !c.update && !c.delete)) && preview.summary.files.count === 0 && !preview.summary.users.some((u: any) => u.op === 'insert') && (
                <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Nincs átviendő változás – a kiválasztott szervezetek a két környezetben megegyeznek.</div>
              )}
              <div className="grid sm:grid-cols-3 gap-2 text-xs">
                <div className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center gap-2"><FileText className="w-4 h-4 text-indigo-500" /> {preview.summary.files.count} fájl átvitele ({fmtBytes(preview.summary.files.bytes)})</div>
                <div className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center gap-2"><Users className="w-4 h-4 text-indigo-500" /> {preview.summary.users.filter((u: any) => u.op === 'insert').length} új felhasználó · {preview.summary.users.filter((u: any) => u.op === 'skip').length} már létezik</div>
                <div className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-emerald-500" /> Végrehajtás előtt automatikus mentés készül a célban</div>
              </div>
              {preview.summary.warnings.length > 0 && (
                <ul className="text-xs text-amber-700 dark:text-amber-400 list-disc pl-5">{preview.summary.warnings.slice(0, 30).map((w: string, i: number) => <li key={i}>{w}</li>)}</ul>
              )}
              <p className="text-[11px] text-slate-500">* A törlés visszaállítható: a bejegyzések és oldalak inaktívvá válnak, a többi tétel a futás naplójából egy kattintással visszatölthető. A fájlok a tárhelyen megmaradnak.</p>
              <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-wrap items-center justify-between gap-2">
                {dstIsProd ? (
                  <label className="text-xs flex items-center gap-2">Éles célba írás – írd be: <b>ÉLES</b>
                    <input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} className="px-2 py-1 rounded border border-rose-300 bg-white dark:bg-slate-950 w-24" />
                  </label>
                ) : <span className="text-xs text-slate-500">A jóváhagyás után a fenti változások végrehajtódnak.</span>}
                <button onClick={doApply} disabled={!confirmOk} className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold flex items-center gap-2 cursor-pointer disabled:opacity-50">
                  <Play className="w-4 h-4" /> Jóváhagyás és végrehajtás
                </button>
              </div>
            </section>
          )}

          {step === 'running' && (
            <section className="p-6 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-3 text-center">
              <RefreshCw className="w-8 h-8 mx-auto text-indigo-500 animate-spin" />
              <div className="text-sm font-semibold">{progress.text}</div>
              <div className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-800 overflow-hidden">
                <div className="h-full bg-indigo-600 transition-all" style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }} />
              </div>
              <p className="text-xs text-slate-500">Ne zárd be az ablakot, amíg a fájlátvitel tart.</p>
            </section>
          )}

          {step === 'report' && report && (
            <section className="space-y-3">
              <div className={`p-4 rounded-xl border ${report.failed.length || report.missing.length ? 'border-amber-300 bg-amber-50 dark:bg-amber-950/30' : 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30'}`}>
                <div className="flex items-center gap-2 font-semibold text-sm"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> A szinkron lefutott</div>
                <ul className="mt-2 text-xs space-y-1">
                  <li>Futás azonosító: <span className="font-mono">{report.run_id}</span></li>
                  <li>Fájlok: {report.sent}/{report.files} átvitelre került</li>
                  {report.failed.map((f: string, i: number) => <li key={i} className="text-amber-700">Nem sikerült: {f}</li>)}
                  {report.warnings.slice(0, 20).map((w: string, i: number) => <li key={`w${i}`} className="text-amber-700">{w}</li>)}
                  {report.stateNote && <li className="text-amber-700">{report.stateNote}</li>}
                </ul>
              </div>
              {report.backups?.length > 0 && (
                <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs space-y-1">
                  <div className="font-semibold">Szinkron előtti mentések a célban</div>
                  {report.backups.map((b: any) => (
                    <button key={b.key} onClick={() => downloadBackup(dstKey, report.run_id, b.tenant_id)} className="flex items-center gap-1 text-indigo-600 hover:underline cursor-pointer"><Download className="w-3.5 h-3.5" /> Szervezet #{b.tenant_id} ({fmtBytes(b.bytes)})</button>
                  ))}
                </div>
              )}
              <div className="flex justify-between">
                <button onClick={() => doRollback(dstKey, report.run_id)} className="px-3 py-2 rounded-xl border border-rose-300 text-rose-600 text-xs font-semibold flex items-center gap-1 cursor-pointer"><RotateCcw className="w-3.5 h-3.5" /> Ennek a futásnak a visszaállítása</button>
                <button onClick={() => { setStep('setup'); setPreview(null); }} className="px-3 py-2 rounded-xl bg-slate-800 dark:bg-slate-200 text-white dark:text-slate-900 text-xs font-semibold cursor-pointer">Új szinkron</button>
              </div>
            </section>
          )}

          {step === 'runs' && (
            <section className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-semibold">Futások itt:</span>
                  <select onChange={(e) => loadRuns(e.target.value)} value={runsKey} className="px-2 py-1 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs">
                    {eps.map((e) => <option key={e.key} value={e.key}>{e.label}</option>)}
                  </select>
                </div>
                <button onClick={() => setStep('setup')} className="text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 cursor-pointer">Vissza</button>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                {runs.length === 0 && <div className="p-4 text-slate-500 text-center">Nincs futás.</div>}
                {runs.map((r) => (
                  <div key={r.id} className="p-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="font-semibold">{r.created_at} · forrás: {r.source_name || r.source_instance_id?.slice(0, 8)}</div>
                      <div className="text-slate-500">{(r.summary?.tenants || []).filter((t: any) => t.mode !== 'skip').map((t: any) => t.name).join(', ')} · állapot: {r.status}</div>
                    </div>
                    {!['rolled_back', 'failed_rolled_back', 'failed'].includes(r.status) && (
                      <button onClick={() => doRollback(runsKey, r.id)} className="px-2 py-1 rounded-lg border border-rose-300 text-rose-600 flex items-center gap-1 cursor-pointer"><RotateCcw className="w-3 h-3" /> Visszaállítás</button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Bejelentkezés távoli környezetbe */}
        {loginFor && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-2xl p-5 space-y-3 border border-slate-200 dark:border-slate-800">
              <div className="text-sm font-bold">Belépés: {ep(loginFor).label}</div>
              <p className="text-[11px] text-slate-500">A célkörnyezet rendszer-superadmin fiókjával. A token csak az ablak bezárásáig él.</p>
              {!loginForm.temp ? (
                <>
                  <input value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="E-mail" className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm" />
                  <input type="password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="Jelszó" className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm" />
                </>
              ) : (
                <input value={loginForm.code} onChange={(e) => setLoginForm({ ...loginForm, code: e.target.value.replace(/\D/g, '') })} placeholder="2FA kód" maxLength={6} className="w-full px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm tracking-widest" />
              )}
              <div className="flex justify-end gap-2">
                <button onClick={() => setLoginFor(null)} className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs cursor-pointer">Mégse</button>
                <button onClick={doLogin} disabled={busy === 'login'} className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold cursor-pointer disabled:opacity-50">Belépés</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnvSyncWizard;
